import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, hasStrokeDraw } from '../skillUsage';

export const svgDrawOnSkill: CodeoSkill = {
    name: SKILL_NAMES.svgDrawOn,
    kind: 'codeo',
    description:
        'Draw a stroke on screen with stroke-dash animation — hand-drawn underlines beneath a ' +
        'key word, traced rings or arcs, growing chart lines, connector paths between elements.',
    documentUsesSkill: check((script) => hasStrokeDraw(script)),
    instructions: `# SVG Draw-On

A stroke draws itself across the screen: set the path's \`stroke-dasharray\` a couple of units *past* its own length, offset it one unit further still, then tween the offset to 0. Reads as hand-drawn intent — an underline appearing under the word as it's spoken, a ring tracing around a number, a chart line growing.

## Markup

\`\`\`html
<svg id="underline" width="420" height="24" viewBox="0 0 420 24"
     style="position: absolute; left: 750px; top: 620px;">
  <path id="underline-path" d="M4 18 C 120 6, 300 6, 416 14"
        fill="none" stroke="var(--l5-color-1, #7a6dff)" stroke-width="6" stroke-linecap="round"/>
</svg>
\`\`\`

A gentle S-curve (\`C\` command with control points a few px off the baseline) reads as hand-drawn; a dead-straight \`L\` line reads as a border. The path is pure geometry.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const underlinePath = document.querySelector('#underline-path');
const underlineLen = Math.ceil(underlinePath.getTotalLength()) + 2;
gsap.set(underlinePath, { strokeDasharray: underlineLen, strokeDashoffset: underlineLen + 1 });
tl.to(underlinePath, { strokeDashoffset: 0, duration: 0.7, ease: 'power2.inOut' }, 2.2);
window.__timelines = { main: tl };
\`\`\`

The build-time \`gsap.set\` hides the stroke before the timeline runs — equivalent to the sanctioned \`gsap.set(el, { opacity: 0 })\` pattern for late entrances.

**The \`+ 2\` and the \`+ 1\` are load-bearing — never set the dash to the raw length, and never set the offset equal to it.** The dash pattern is \`dash\` then an equal gap, restarted at each subpath, and a point at distance \`p\` along the path is painted when \`(p + offset) mod (2 × dash)\` falls under \`dash\`. Set both to the same measured length and the path's ends land exactly on that boundary — where the sub-unit slop between the two (GSAP writes \`strokeDashoffset\` rounded to a whole number while \`strokeDasharray\` keeps its decimals) leaves a fraction of a unit painted. **With round caps, a dash shorter than the stroke is drawn as a full-width dot**, so the "hidden" stroke is really a dot sitting on screen for the whole hold before the draw — and one path with several \`M\` commands parks one per subpath, a constellation that reads as deliberate marks the lines then grow out of.

Padding the dash past the path and parking the offset one unit into the gap is what actually hides it: the whole path then sits strictly inside the gap, clear of the boundary at both ends. Offsetting to \`length + 1\` against an unpadded dash is *not* enough — it clears the start and the pattern wraps around to paint the same dot at the end instead.

## Adapt

- **Underline a spoken word**: position the draw at the word's onset, duration 0.5–0.8, \`power2.inOut\`.
- **Ring/circle around a value**: a circular path (or \`<circle>\` — \`getTotalLength\` works on it) drawn over ~0.9s lands well started just before the value's onset.
- **Chart line**: duration 1.2–2.0 with \`power1.inOut\`; pair each vertex's arrival with that data point's spoken onset if narrated.
- **Reverse direction**: don't tween \`strokeDashoffset\` to a negative length — flip the path's direction in the \`d\` attribute instead; negative offsets double-draw on some path shapes.
- **Draw-off exit**: tween offset from 0 back to \`underlineLen + 1\` — the value \`gsap.set\` parked it at, not the path's own length. Reversing to the dash length lands on the boundary, which leaves a cap dot behind on a closed shape like a ring.

## Pitfalls

- Give the \`<svg>\` explicit \`width\`/\`height\` attributes — a viewBox-only svg with no explicit size won't size predictably in the DOM and can stretch to fill its container.
- \`getTotalLength()\` needs the real path in the DOM; the timeline script runs after the markup, so this just works — don't move it into a callback.
- Round caps (\`stroke-linecap: round\`) sell the hand-drawn read; butt caps look mechanical.
- The draw reveals geometry, not text — the word being underlined still needs its own entrance tween; drawing the underline doesn't substitute for revealing the word.`,
};
